// import {store} from '../redux';
import { timeout } from "./general";
import moment from "moment";
import setCookieParser from "set-cookie-parser";
import { ApiError } from "./error";
import { API_ENDPOINTS } from "./constant";
import pkceChallenge from "pkce-challenge";
import { userLogout } from "../sagas/actions";
import { isAuthFailureResponse, handleAuthFailure } from "./authFailure";

const AUTH_CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID || "";
const AUTH_CLIENT_SECRET = import.meta.env.VITE_AUTH_CLIENT_SECRET || "";
const DEFAULT_AUTH_SCOPE = import.meta.env.VITE_DEFAULT_AUTH_SCOPE || "";

let store;
export const injectStoreApi = (_store) => {
  store = _store;
};

const getAuthState = () => {
  if (!store || typeof store.getState !== "function") {
    return {};
  }
  return store.getState()?.auth || {};
};

const getLoginResponse = () => getAuthState().loginResponse || null;

const getAccessToken = ({ fallbackToAnon = false } = {}) => {
  const auth = getAuthState();
  const token = auth?.loginResponse?.access_token;
  if (token) return token;
  if (fallbackToAnon) return auth?.anonToken || "";
  return "";
};

const getRefreshToken = () =>
  getAuthState()?.loginResponse?.refresh_token || "";

const getAnonToken = () => getAuthState()?.anonToken || "";

// HTML responses
const RESPONSE_CODE = {
  UNAUTHORIZED: 401,
  ACESS_DENIED: 403,
  NOT_ACCEPTABLE: 406,
};

const CSRF_ERROR_MSG = "CSRF validation failed";

const isOauthTokenUrl = (url = "") => url.includes("/oauth/token");

const toOauthFormBody = (body = {}) => {
  const params = new URLSearchParams();
  Object.entries(body || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === "object") {
      return;
    }
    params.append(key, String(value));
  });
  return params.toString();
};

let pkcePromise;
const ensurePkce = async () => {
  if (!pkcePromise) {
    pkcePromise = pkceChallenge().then((pkce) => {
      sessionStorage.setItem("codeVerifier", pkce.code_verifier);
      return pkce;
    });
  }
  return pkcePromise;
};

const parseSetCookie = (response) => {
  const combinedCookieHeader = response.headers.get("Set-Cookie");
  if (!combinedCookieHeader) {
    return {};
  }
  const splitCookieHeaders =
    setCookieParser.splitCookiesString(combinedCookieHeader);
  const setCookies = setCookieParser.parse(splitCookieHeaders);

  let setCookie = { cookie: combinedCookieHeader };

  let expires;
  setCookies.forEach((c) => {
    const { expires: e } = c;
    if (!expires) {
      expires = e;
      return;
    }
    expires = moment(expires) < moment(e) ? expires : e;
  });

  setCookie.expires = expires;
  return setCookie;
};

// Shared in-flight refresh promise – ensures concurrent callers (pre-flight,
// 401 retry, saga background refresh, App.jsx session validation) all share
// one HTTP call and one UPDATE_ACCESS_TOKEN dispatch per refresh cycle.
let _refreshInFlight = null;

export const ensureFreshToken = async () => {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    const currentRefreshToken = getRefreshToken();
    if (!currentRefreshToken) throw new Error("No refresh token available");
    const payload = {
      client_id: AUTH_CLIENT_ID,
      grant_type: "refresh_token",
      refresh_token: currentRefreshToken,
      client_secret: AUTH_CLIENT_SECRET,
      // scope is intentionally omitted: RFC 6749 §6 makes it optional for
      // refresh_token grants and Drupal simple_oauth rejects it with
      // invalid_scope when the value doesn't exactly match the stored grant.
    };
    const res = await fetch(API_ENDPOINTS.refreshUserToken, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: toOauthFormBody(payload),
    });
    if (!res.ok)
      throw new Error(`Token refresh failed with status ${res.status}`);
    const data = await res.json();
    if (!data?.access_token)
      throw new Error(
        data?.message || "Token refresh returned no access_token",
      );
    const expiryTime =
      Date.now() + (data.expires_in || 3600) * 1000 - 60 * 1000;
    store?.dispatch?.({
      type: "UPDATE_ACCESS_TOKEN",
      response: { ...data, expiryTime },
    });
    return data.access_token;
  })().finally(() => {
    _refreshInFlight = null;
  });
  return _refreshInFlight;
};

/* addition functionality has been added to traditional POST api method, which is called authorizedFetch. 
This custom post method:
- sets a cookie, expires and token automatically
- handles 401 and 403 errors 
- sets the headers to accept: application/json and Content-Type: application/json
- stringifies json API body
This is where we can centralize some error handling and utilize the, previously implemented, try/catch design pattern 
*/
const authorizedFetch = async (
  url,
  body = null,
  method = "POST",
  defaultLoad = true,
  abort,
) => {
  let accessToken = getAccessToken({ fallbackToAnon: false });
  let errorMsg;
  let responsePayload;

  // Pre-flight: if the stored token is already past its expiry, refresh before sending
  if (!isOauthTokenUrl(url)) {
    const loginResp = store?.getState?.()?.auth?.loginResponse;
    if (
      loginResp?.access_token &&
      loginResp?.expiryTime &&
      Date.now() >= loginResp.expiryTime
    ) {
      try {
        accessToken = await ensureFreshToken();
      } catch (_preflightErr) {
        // fall through – the 401 retry below will handle it
      }
    }
  }

  try {
    const useOauthFormBody = isOauthTokenUrl(url);
    // Set headers with token.
    const headers = {
      "Content-Type": useOauthFormBody
        ? "application/x-www-form-urlencoded"
        : "application/json",
      Accept: "application/json",
    };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    // Request options
    let options = {
      method,
      headers,
      credentials: "omit",
      body: useOauthFormBody ? toOauthFormBody(body) : JSON.stringify(body),
    };

    if (abort) {
      options = { ...options, signal: abort.signal };
    }

    // Make the request
    console.info("Posting to:", { url }, "with options:", options);
    console.log(body);
    console.log("Body STR : ", JSON.stringify(body));
    let response = await fetch(url, options);

    // 401 retry: token may have expired between pre-flight check and the actual request
    if (
      response.status === RESPONSE_CODE.UNAUTHORIZED &&
      !isOauthTokenUrl(url)
    ) {
      try {
        const refreshedToken = await ensureFreshToken();
        headers.Authorization = `Bearer ${refreshedToken}`;
        response = await fetch(url, options);
      } catch (_retryErr) {
        // refresh failed – fall through with the 401 response
      }
    }

    // Parse response
    responsePayload = await response.json();
    console.log("Response Payload : " + JSON.stringify(responsePayload));
    const setCookie = parseSetCookie(response);
    errorMsg = responsePayload.message;

    // Handle auth failures: invalid_scope or denied-request responses from
    // an established authenticated session must force a clean logout.
    if (isAuthFailureResponse(responsePayload)) {
      handleAuthFailure();
    }

    // console.log(responsePayload.message == "Access token has been revoked")
    if (
      responsePayload.message &&
      responsePayload.message == "Access token has been revoked"
    ) {
      store?.dispatch?.(userLogout());
    }

    // Handle errors
    if (!response.ok) {
      throw new ApiError(responsePayload);
    }

    return { ...responsePayload, ...setCookie };
  } catch (err) {
    console.error("Fetch error:", JSON.stringify(err));
    // return { status: 'Failed', message: errorMsg };
    return responsePayload;
  }
};

export const authorizedFetchPDF = async (
  url,
  body = null,
  method = "POST",
  defaultLoad = true,
  abort,
) => {
  let accessToken = getAccessToken({ fallbackToAnon: true });
  let response, headers, options;

  try {
    headers = {
      "Content-Type": "application/json",
      Accept: "application/pdf",
    };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    options = {
      method,
      headers,
      credentials: "omit",
      body: body != null ? JSON.stringify(body) : null,
    };

    if (abort) options = { ...options, signal: abort.signal };

    // first try
    response = await fetch(url, options);

    // if expired, try to refresh token once
    // if (response.status === 401) {
    //   const newToken = await API.refreshUserTokenAfterExpiry();
    //   headers.Authorization = `Bearer ${newToken}`;
    //   response = await fetch(url, options);
    // }

    if (!response.ok) {
      // try to parse JSON error payload
      let errPayload = {};
      try {
        errPayload = await response.json();
      } catch {}
      if (isAuthFailureResponse(errPayload)) {
        handleAuthFailure();
      }
      throw new ApiError(errPayload);
    }
    let filename = "";
    const cd =
      response.headers.get("Content-Disposition") ||
      response.headers.get("Content-Disposition");
    if (cd) {
      const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
      if (match) filename = decodeURIComponent(match[1] || match[2]);
    }

    // pull back the PDF blob
    const blob = await response.blob();

    // propagate any set-cookie headers if needed
    const setCookie = parseSetCookie(response);

    return { blob, filename, ...setCookie };
  } catch (err) {
    console.error("PDF fetch error:", err);
    if (err.message?.includes("Access token has been revoked")) {
      store?.dispatch?.(userLogout());
    }
    throw err;
  }
};

export const authorizedFetchCSV = async (
  url,
  body = null,
  method = "POST",
  abort,
) => {
  let accessToken = getAccessToken({ fallbackToAnon: true });

  let response;

  const headers = {
    "Content-Type": "application/json",
    Accept: "text/csv,application/csv",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  let options = {
    method,
    headers,
    credentials: "omit",
    body: body != null ? JSON.stringify(body) : null,
  };

  if (abort) options.signal = abort.signal;

  try {
    // first pass
    response = await fetch(url, options);

    // if token expired, try refreshing once
    // if (response.status === 401) {
    //   const newToken = await API.refreshUserTokenAfterExpiry();
    //   headers.Authorization = `Bearer ${newToken}`;
    //   response = await fetch(url, options);
    // }

    if (!response.ok) {
      // try to decode any JSON error message
      let errPayload = {};
      try {
        errPayload = await response.json();
      } catch {}
      if (isAuthFailureResponse(errPayload)) {
        handleAuthFailure();
      }
      throw new ApiError(errPayload);
    }

    // pull filename out of headers if provided
    let filename = "";
    const cd = response.headers.get("Content-Disposition");
    if (cd) {
      const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i);
      if (m) filename = decodeURIComponent(m[1] || m[2]);
    }

    // grab the CSV blob
    const blob = await response.blob();

    // propagate any set-cookie headers if you care
    const setCookie = parseSetCookie(response);

    return { blob, filename, ...setCookie };
  } catch (err) {
    console.error("CSV fetch error:", err);
    if (err.message?.includes("Access token has been revoked")) {
      store?.dispatch?.(userLogout());
    }
    throw err;
  }
};

const authorizedFetchAnon = async (
  url,
  body = null,
  method = "POST",
  defaultLoad = true,
  abort,
) => {
  let errorMsg;
  let responsePayload;
  try {
    const useOauthFormBody = isOauthTokenUrl(url);
    // No Authorization header for anonymous calls
    const headers = {
      "Content-Type": useOauthFormBody
        ? "application/x-www-form-urlencoded"
        : "application/json",
      Accept: "application/json",
    };

    // Request options
    let options = {
      method,
      headers,
      credentials: "omit",
      body: useOauthFormBody ? toOauthFormBody(body) : JSON.stringify(body),
    };

    if (abort) {
      options = { ...options, signal: abort.signal };
    }

    // Make the request
    // console.info('Posting to:', { url }, 'with options:', options);
    // console.log(body);
    // console.log("Body STR : " , JSON.stringify(body))
    let response = await fetch(url, options);

    // If unauthorized, refresh token and retry once
    // if (response.status === RESPONSE_CODE.UNAUTHORIZED) {
    //   accessToken = await refreshAccessToken();
    //   headers.Authorization = `Bearer ${accessToken}`;
    //   response = await fetch(url, options);
    // }

    // Parse response
    responsePayload = await response.json();
    // console.log("Response Payload for " + url + ":" + JSON.stringify(responsePayload))
    const setCookie = parseSetCookie(response);
    errorMsg = responsePayload.message;

    // console.log(responsePayload.message == "Access token has been revoked")
    if (
      responsePayload.message &&
      responsePayload.message == "Access token has been revoked"
    ) {
      store?.dispatch?.(userLogout());
    }

    // Handle errors
    if (!response.ok) {
      throw new ApiError(responsePayload);
    }

    return { ...responsePayload, ...setCookie };
  } catch (err) {
    console.error("Fetch error:", JSON.stringify(err));
    // return { status: 'Failed', message: errorMsg };
    return responsePayload;
  }
};

const authorizedFetchAnonWithCaptcha = async (
  url,
  body = null,
  method = "POST",
  defaultLoad = true,
  abort,
  captcha_token,
) => {
  let errorMsg;
  let responsePayload;
  try {
    const useOauthFormBody = isOauthTokenUrl(url);
    // No Authorization header for anonymous calls
    const headers = {
      "Content-Type": useOauthFormBody
        ? "application/x-www-form-urlencoded"
        : "application/json",
      Accept: "application/json",
      "captcha-token": captcha_token,
    };

    // Request options
    let options = {
      method,
      headers,
      credentials: "omit",
      body: useOauthFormBody ? toOauthFormBody(body) : JSON.stringify(body),
    };

    if (abort) {
      options = { ...options, signal: abort.signal };
    }

    // Make the request
    // console.info('Posting to:', { url }, 'with options:', options);
    // console.log(body);
    // console.log("Body STR : " , JSON.stringify(body))
    let response = await fetch(url, options);

    // If unauthorized, refresh token and retry once
    // if (response.status === RESPONSE_CODE.UNAUTHORIZED) {
    //   accessToken = await refreshAccessToken();
    //   headers.Authorization = `Bearer ${accessToken}`;
    //   response = await fetch(url, options);
    // }

    // Parse response
    responsePayload = await response.json();
    // console.log("Response Payload for " + url + ":" + JSON.stringify(responsePayload))
    const setCookie = parseSetCookie(response);
    errorMsg = responsePayload.message;

    // console.log(responsePayload.message == "Access token has been revoked")
    if (
      responsePayload.message &&
      responsePayload.message == "Access token has been revoked"
    ) {
      store?.dispatch?.(userLogout());
    }

    // Handle errors
    if (!response.ok) {
      throw new ApiError(responsePayload);
    }

    return { ...responsePayload, ...setCookie };
  } catch (err) {
    console.error("Fetch error:", JSON.stringify(err));
    // return { status: 'Failed', message: errorMsg };
    return responsePayload;
  }
};

const authorizedGet = async (url, query = {}) => {
  let accessToken = getAccessToken({ fallbackToAnon: false });

  // Create headers with Authorization token
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  // Append query parameters to the URL
  const queryString = Object.keys(query)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`,
    )
    .join("&");

  const fullUrl = queryString ? `${url}?${queryString}` : url;

  const options = {
    method: "GET",
    headers,
  };

  try {
    console.info("Fetching from:", { fullUrl }, "with options:", options);
    let response = await fetch(fullUrl, options);

    // Refresh token and retry once if unauthorized
    // if (response.status === RESPONSE_CODE.UNAUTHORIZED) {
    //   accessToken = await API.refreshUserTokenAfterExpiry()
    //   headers.Authorization = `Bearer ${accessToken}`;
    //   response = await fetch(fullUrl, options);
    // }

    // Parse the response
    const responsePayload = await response.json();
    console.log("Response Payload : " + JSON.stringify(responsePayload));

    // Handle auth failures on authenticated GET calls
    if (isAuthFailureResponse(responsePayload)) {
      handleAuthFailure();
    }

    // Throw an error if the response is not OK
    if (!response.ok) {
      throw new ApiError(responsePayload);
    }

    return responsePayload;
  } catch (err) {
    console.error("Fetch error:", err);
    return { status: "Failed", message: err.toString() };
  }
};

class API {
  /* Auth */

  static getUserToken = async () => {
    const payload = {
      client_id: AUTH_CLIENT_ID,
      grant_type: "client_credentials",
      client_secret: AUTH_CLIENT_SECRET,
      scope: DEFAULT_AUTH_SCOPE,
    };
    const response = await fetch(API_ENDPOINTS.userToken, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: toOauthFormBody(payload),
    });

    const responsePayload = await response.json();
    return responsePayload;

    // if (response.ok && responsePayload.access_token) {
    //   store.dispatch({
    //     type: 'STORE_ANONYMOUS_TOKEN',
    //     anonToken: responsePayload.access_token,
    //   });
    //   return responsePayload.access_token;
    // }
    // throw new ApiError('Failed to retrieve anonymous token');
  };

  static refreshUserTokenAfterExpiry = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const payload = {
      client_id: AUTH_CLIENT_ID,
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_secret: AUTH_CLIENT_SECRET,
      scope: DEFAULT_AUTH_SCOPE,
    };
    const res = await fetch(API_ENDPOINTS.refreshUserToken, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: toOauthFormBody(payload),
    });

    if (!res.ok) {
      // you might want to parse the JSON error here
      throw new Error(`Refresh failed with status ${res.status}`);
    }

    const responsePayload = await res.json();
    return responsePayload;
  };

  static loginOTP = async (email, captchaToken, honeypotValue) => {
    const payload = { email: email };
    if (captchaToken) payload.captcha_token = captchaToken;
    if (honeypotValue) payload.website_url = honeypotValue;
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.loginOTP,
      payload,
    );
    return responsePayload;
  };

  static verifyLoginOTP = async (email, otp, captchaToken, honeypotValue) => {
    const payload = { email, otp };
    if (captchaToken) payload.captcha_token = captchaToken;
    if (honeypotValue) payload.website_url = honeypotValue;
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.verifyLoginOTP,
      payload,
    );
    return responsePayload;
  };

  static encryptTokens = async (token, email) => {
    const responsePayload = await authorizedFetch(API_ENDPOINTS.encryptTokens, {
      token,
      email,
    });
    return responsePayload;
  };

  static userLogin = async (email, password, captcha_token, options = {}) => {
    const {
      grant_type: overrideGrantType,
      is_osceola_classlink_sso,
      client_id: overrideClientId,
      client_secret: overrideClientSecret,
      skipCodeChallenge,
      ...extraPayload
    } = options || {};

    const payload = {
      client_id: overrideClientId || AUTH_CLIENT_ID,
      grant_type: overrideGrantType || "password",
      username: email,
      password,
      client_secret: overrideClientSecret || AUTH_CLIENT_SECRET,
      ...extraPayload,
    };

    if (!skipCodeChallenge) {
      const { code_challenge } = await ensurePkce();
      payload.code_challenge = code_challenge;
      payload.code_challenge_method = "S256";
    }

    if (typeof is_osceola_classlink_sso !== "undefined") {
      payload.is_osceola_classlink_sso = is_osceola_classlink_sso;
    }

    const responsePayload = await authorizedFetchAnonWithCaptcha(
      API_ENDPOINTS.userLogin,
      payload,
      "POST",
      true,
      null,
      captcha_token,
    );
    return responsePayload;
  };

  static fetchPixel = async () => {
    try {
      const headers = {
        Accept: "image/*,application/octet-stream;q=0.9,*/*;q=0.8",
        "Cache-Control": "no-cache",
      };

      const response = await fetch(API_ENDPOINTS.pixel, {
        method: "GET",
        mode: "cors",
        headers,
        credentials: "include",
      });

      const token =
        response.headers.get("X-Pixel-Token") ||
        response.headers.get("x-pixel-token") ||
        "";

      // consume body (may be a pixel image) to avoid locking the stream
      try {
        await response.blob();
      } catch (e) {
        // ignore body read errors; token is header-based
      }

      if (!response.ok) {
        throw new Error(`Pixel request failed with status ${response.status}`);
      }

      if (!token) {
        throw new Error("Pixel token missing from response headers");
      }

      return token.trim();
    } catch (error) {
      console.error("Pixel fetch error:", error);
      throw error;
    }
  };

  static getSchoolByZip = async (profileData) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSchoolByZip,
      profileData,
    );
    return responsePayload;
  };
  static getZipByState = async (profileData) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getZipByState,
      profileData,
    );
    return responsePayload;
  };
  static registerUser = async (profileData) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.registerUser,
      profileData,
    );
    return responsePayload;
  };

  static getContactType = async (profileData) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getContactType,
      profileData,
    );
    return responsePayload;
  };

  static getSchoolLevel = async () => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSchoolLevel,
      {},
    );
    return responsePayload;
  };

  static getSchoolLevels = async () => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSchoolLevels,
      {},
    );
    return responsePayload;
  };

  static getCountries = async () => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getCountries,
      {},
    );
    return responsePayload;
  };

  static refreshUserToken = async () => {
    const refreshToken = getRefreshToken();
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.refreshUserToken,
      {
        client_id: AUTH_CLIENT_ID,
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_secret: AUTH_CLIENT_SECRET,
      },
    );
    return responsePayload;
  };

  static getUserProfile = async () => {
    const responsePayload = await authorizedGet(API_ENDPOINTS.getUserProfile);
    return responsePayload;
  };

  static getSubscriptionVideos = async () => {
    const responsePayload = await authorizedGet(
      API_ENDPOINTS.getSubscriptionVideos,
    );
    return responsePayload;
  };

  static getAnnouncements = async () => {
    const responsePayload = await authorizedGet(API_ENDPOINTS.getAnnouncements);
    return responsePayload;
  };
  static getCurriculum = async () => {
    const responsePayload = await authorizedGet(API_ENDPOINTS.getCurriculum);
    return responsePayload;
  };

  static updateUserProfile = async (profileData) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateUserProfile,
      profileData,
    );
    return responsePayload;
  };
  static newEmailOTP = async (email) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateNewEmailOTP,
      {
        email: email,
      },
    );
    return responsePayload;
  };
  static verifyNewEmailOTP = async (otp) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.verifyUpdateNewEmailOTP,
      {
        otp: otp,
      },
    );
    return responsePayload;
  };
  // Verifies the OTP and sends the reset link in one request. This previously
  // relied on a separate verifyLoginOTP call happening first; the backend now
  // verifies the OTP itself, so the reset email can no longer be lost when the
  // second call fails to fire.
  static forgetPassword = async (email, otp, captchaToken, honeypotValue) => {
    const payload = {
      email: email,
      otp: otp,
    };
    if (captchaToken) payload.captcha_token = captchaToken;
    if (honeypotValue) payload.website_url = honeypotValue;
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.forgetPassword,
      payload,
    );
    return responsePayload;
  };

  static forgetPasswordReset = async (
    email,
    code,
    password,
    c_password,
    captchaToken,
  ) => {
    const payload = {
      email: email,
      code: code,
      password: password,
      c_password: c_password,
    };
    if (captchaToken) payload.captcha_token = captchaToken;
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.forgetPasswordReset,
      payload,
    );
    return responsePayload;
  };
  static updateUserEmail = async (email, userId) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateUserEmail,
      {
        email: email,
        user_id: userId,
      },
    );
    return responsePayload;
  };

  static passwordReset = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.passwordReset,
      payload,
    );
    return responsePayload;
  };

  /* ILEARN APIS */

  static getILEARNModules = async () => {
    const responsePayload = await authorizedGet(API_ENDPOINTS.getILEARNModules);
    return responsePayload;
  };

  // static getILEARNChapters = async (module_no) => {
  //   const responsePayload = await authorizedFetch(
  //     API_ENDPOINTS.getILEARNChapters,
  //     {
  //       module_no: module_no,
  //     }
  //   );
  //   return responsePayload;
  // }

  static getILEARNChapters = async () => {
    const responsePayload = await authorizedGet(
      API_ENDPOINTS.getILEARNChapters,
    );
    return responsePayload;
  };

  static getILEARNChapter = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getILEARNChapter,
      payload,
    );
    return responsePayload;
  };

  static getILEARNUser = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getILEARNUser,
      payload,
    );
    return responsePayload;
  };

  static updateILEARNUser = async (userObject) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateILEARNUser,
      userObject,
    );
    return responsePayload;
  };

  static getILearnVideos = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getILearnVideos,
      payload,
    );
    return responsePayload;
  };

  static getUserCertificate = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getUserCertificate,
      payload,
    );
    return responsePayload;
  };

  /* Admin Central APIS */

  static getPOCChanges = async () => {
    const responsePayload = await authorizedGet(API_ENDPOINTS.getPOCChanges);
    return responsePayload;
  };

  // static handlePOCReject = async (customer_id) => {
  //   const responsePayload = await authorizedFetch(
  //     API_ENDPOINTS.rejectPOC,
  //     {
  //       customer_id: customer_id,
  //     },
  //   );
  //   return responsePayload;
  // }

  static getAdminRoles = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getAdminRoles,
      {},
    );
    return responsePayload;
  };
  static getAdmins = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getAdmins,
      payload,
    );
    return responsePayload;
  };
  static addAdmin = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addAdmin,
      payload,
    );
    return responsePayload;
  };
  static revokeAdmin = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.revokeAdmin,
      payload,
    );
    return responsePayload;
  };
  static getOrganizationType = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getOrganizationType,
      payload,
    );
    return responsePayload;
  };
  static addOrganizationType = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addOrganizationType,
      payload,
    );
    return responsePayload;
  };
  static updateOrganizationType = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateOrganizationType,
      payload,
    );
    return responsePayload;
  };
  static deleteOrganizationType = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.deleteOrganizationType,
      payload,
    );
    return responsePayload;
  };
  static getProductCodes = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getProductCodes,
      payload,
    );
    return responsePayload;
  };
  static addProductCodes = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addProductCodes,
      payload,
    );
    return responsePayload;
  };
  static updateProductCodes = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateProductCodes,
      payload,
    );
    return responsePayload;
  };
  static deleteProductCode = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.deleteProductCode,
      payload,
    );
    return responsePayload;
  };
  static getSalesRegion = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSalesRegion,
      payload,
    );
    return responsePayload;
  };
  static addSalesRegions = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addSalesRegions,
      payload,
    );
    return responsePayload;
  };
  static updateSalesRegion = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateSalesRegion,
      payload,
    );
    return responsePayload;
  };
  static getSalesPerson = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSalesPerson,
      payload,
    );
    return responsePayload;
  };
  static addSalesPerson = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addSalesPerson,
      payload,
    );
    return responsePayload;
  };
  static updateSalesPerson = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateSalesPerson,
      payload,
    );
    return responsePayload;
  };
  static getWebinars = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getWebinars,
      payload,
    );
    return responsePayload;
  };
  static addWebinar = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addWebinar,
      payload,
    );
    return responsePayload;
  };

  static editWebinars = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.editWebinars,
      payload,
    );
    return responsePayload;
  };

  static editWebinar = async (payload) => {
    return API.editWebinars(payload);
  };
  static getWebinarRegistered = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getWebinarsRegistered,
      payload,
    );
    return responsePayload;
  };
  static getEmailLog = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getEmailLog,
      payload,
    );
    return responsePayload;
  };
  static searchOrganizations = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.searchOrganizations,
      payload,
    );
    return responsePayload;
  };
  static getOrganizationDetail = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getOrganizationDetail,
      payload,
    );
    return responsePayload;
  };
  static searchUsers = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.searchUsers,
      payload,
    );
    return responsePayload;
  };
  static getUserDetail = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getUserDetail,
      payload,
    );
    return responsePayload;
  };
  static blockUser = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.blockUser,
      payload,
    );
    return responsePayload;
  };
  static unblockUser = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.unblockUser,
      payload,
    );
    return responsePayload;
  };
  static getSubscriptionListing = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSubscriptionListing,
      payload,
    );
    return responsePayload;
  };
  static getSubscriptionDetail = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSubscriptionDetail,
      payload,
    );
    return responsePayload;
  };

  static checkSTATImportRequirement = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.checkSTATImportRequirement,
      payload,
    );
    return responsePayload;
  };

  static getResources = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getResources,
      payload,
    );
    return responsePayload;
  };

  static addResources = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addResources,
      payload,
    );
    return responsePayload;
  };

  static deleteResources = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.deleteResources,
      payload,
    );
    return responsePayload;
  };

  static getRequestedQuotes = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getRequestedQuotes,
      payload,
    );
    return responsePayload;
  };

  static processRequestedQuotes = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.processRequestedQuotes,
      payload,
    );
    return responsePayload;
  };

  static getSettings = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSettings,
      payload,
    );
    return responsePayload;
  };

  static updateSettings = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updatedSettings,
      payload,
    );
    return responsePayload;
  };

  /* Teachers */

  static getSubscriptionTeachers = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSubscriptionTeachers,
      payload,
    );
    return responsePayload;
  };

  static sendWelcomeEmails = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.sendWelcomeEmails,
      payload,
    );
    return responsePayload;
  };

  static getTeacherDetail = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getTeacherDetail,
      payload,
    );
    return responsePayload;
  };
  static selfImplementation = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.selfImplementation,
      payload,
    );
    return responsePayload;
  };

  static resetUserPassword = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.resetUserPassword,
      payload,
    );
    return responsePayload;
  };

  static editSubscriptionTeacher = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.editSubscriptionTeacher,
      payload,
    );
    return responsePayload;
  };
  static denySubscriptionTeacher = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.denySubscriptionTeacher,
      payload,
    );
    return responsePayload;
  };
  static certifySubscriptionTeacher = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.certifySubscriptionTeacher,
      payload,
    );
    return responsePayload;
  };
  static uncertifySubscriptionTeacher = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.uncertifySubscriptionTeacher,
      payload,
    );
    return responsePayload;
  };
  static confirmAllCertification = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.confirmAllCertification,
      payload,
    );
    return responsePayload;
  };

  static certifyAllImplementation = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.certifyAllImplementation,
      payload,
    );
    return responsePayload;
  };
  static confirmAllUncertification = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.confirmAllUncertification,
      payload,
    );
    return responsePayload;
  };
  static uncertifyAllImplementation = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.uncertifyAllImplementation,
      payload,
    );
    return responsePayload;
  };
  static removeSubscriptionTeacher = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.removeSubscriptionTeacher,
      payload,
    );
    return responsePayload;
  };
  static exportTeachersList = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.exportTeachersList,
      payload,
    );
    return responsePayload;
  };

  static verifyTeacherEmail = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.verifyTeacherEmail,
      payload,
    );
    return responsePayload;
  };

  static importTeacherList = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.importTeacherList,
      payload,
    );
    return responsePayload;
  };
  static addSubscriptionTeacher = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addSubscriptionTeacher,
      payload,
    );
    return responsePayload;
  };
  static getSubscriptionSchools = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSubscriptionSchools,
      payload,
    );
    return responsePayload;
  };
  static getSchools = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSchools,
      payload,
    );
    return responsePayload;
  };
  static exportSchoolsList = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.exportSchoolList,
      payload,
    );
    return responsePayload;
  };
  static importSchoolsList = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.importSchoolList,
      payload,
    );
    return responsePayload;
  };
  static importSchools = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.importSchool,
      payload,
    );
    return responsePayload;
  };
  static searchNCES = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.searchNCES,
      payload,
    );
    return responsePayload;
  };
  static searchNCESID = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.searchNCESID,
      payload,
    );
    return responsePayload;
  };
  static removeSchool = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.removeSchool,
      payload,
    );
    return responsePayload;
  };

  static changeSchool = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.changeSchool,
      payload,
    );
    return responsePayload;
  };

  static getPOC = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getPOC,
      payload,
    );
    return responsePayload;
  };
  static rejectPOC = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.rejectPOC,
      payload,
    );
    return responsePayload;
  };
  static approvePOC = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.approvePOC,
      payload,
    );
    return responsePayload;
  };

  static updatePOC = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updatePOC,
      payload,
    );
    return responsePayload;
  };
  static getReportLogs = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getReportLog,
      payload,
    );
    return responsePayload;
  };
  static getAdminAnnouncements = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getAdminAnnouncements,
      payload,
    );
    return responsePayload;
  };
  static addAdminAnnouncements = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addAdminAnnouncements,
      payload,
    );
    return responsePayload;
  };
  static editAdminAnnouncement = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.editAdminAnnouncements,
      payload,
    );
    return responsePayload;
  };
  static editAdminAnnouncements = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.editAdminAnnouncements,
      payload,
    );
    return responsePayload;
  };
  static deleteAdminAnnouncements = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.deleteAdminAnnouncements,
      payload,
    );
    return responsePayload;
  };
  static adminMergeAccount = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.adminMergeAccount,
      payload,
    );
    return responsePayload;
  };

  static adminSearchOrgsForMerge = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.adminSearchOrgsForMerge,
      payload,
    );
    return responsePayload;
  };

  static adminMergeOrganizations = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.adminMergeOrganizations,
      payload,
    );
    return responsePayload;
  };

  static importInvoice = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.importInvoice,
      payload,
    );
    return responsePayload;
  };

  static getTeacherProfile = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getTeacherProfile,
      payload,
    );
    return responsePayload;
  };

  static getTeacherImplementations = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getTeacherImplementations,
      payload,
    );
    return responsePayload;
  };

  static getTeacherImplementationsNew = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getTeacherImplementationsNew,
      payload,
    );
    return responsePayload;
  };

  static countUserLogin = async (payload = {}) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.countUserLogin,
      payload,
    );
    return responsePayload;
  };

  static getTeacherSubscriptions = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getTeacherSubscriptions,
      payload,
    );
    return responsePayload;
  };

  static updateTeacherImplementation = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.updateTeacherImplementation,
      payload,
    );
    return responsePayload;
  };

  static getTeachersBySchool = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getTeachersBySchool,
      payload,
    );
    return responsePayload;
  };

  static getCityByState = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getCityByState,
      payload,
    );
    return responsePayload;
  };
  static getSchoolsByCity = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSchoolsByCity,
      payload,
    );
    return responsePayload;
  };
  static getSchoolId = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSchoolId,
      payload,
    );
    return responsePayload;
  };

  /* Quotes */

  static submitRenewalQuote = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.submitRenewalQuote,
      payload,
    );
    return responsePayload;
  };

  static getRenewalQuote = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getRenewalQuote,
      payload,
    );
    return responsePayload;
  };

  static requestInternalQuote = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.requestInternalQuote,
      payload,
    );
    return responsePayload;
  };

  static getExternalQuote = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getExternalQuote,
      payload,
    );
    return responsePayload;
  };
  static exportQuotePDF = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.exportQuotePDF,
      payload,
    );
    return { blob, filename };
  };

  static exportQuoteCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.exportQuoteCSV,
      payload,
    );
    return { blob, filename };
  };

  static reimportStatSheet = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.reimportStatSheet,
      payload,
    );
    return responsePayload;
  };

  static getSTATs = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getSTATs,
      payload,
    );
    return responsePayload;
  };

  static exportAllQuotesCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.exportAllQuotesCSV,
      payload,
    );
    return { blob, filename };
  };

  /* ISAFE AI*/

  static getChatbotResponse = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getChatbotResponse,
      payload,
    );
    return responsePayload;
  };

  /*Reports*/

  static downloadCertificationReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadCertificationReport,
      payload,
    );
    return { blob, filename };
  };

  static downloadEducatorReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadEducatorReport,
      payload,
    );
    return { blob, filename };
  };

  static downloadERATECertReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadERATECertReport,
      payload,
    );
    return { blob, filename };
  };

  static downloadPendingERATECertReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadPendingERATECertReport,
      payload,
    );
    return { blob, filename };
  };

  static downloadComplianceDocReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadComplianceDocReport,
      payload,
    );
    return { blob, filename };
  };

  static downloadEducatorModuleDownloadReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadEducatorModuleDownloadReport,
      payload,
    );
    return { blob, filename };
  };

  static downloadOtherCSMReport = async (payload) => {
    const { blob, filename } = await authorizedFetchPDF(
      API_ENDPOINTS.downloadOtherCSMReport,
      payload,
    );
    return { blob, filename };
  };

  /* CSV Reports */

  static downloadCertificationReportCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadCertificationReportCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadEducatorReportCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadEducatorReportCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadERATECertReportCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadERATECertReportCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadPendingERATECertReportCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadPendingERATECertReportCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadEducatorParticipatingCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadEducatorParticipatingCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadModuleDownloadCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadModuleDownloadCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadEdBrowserDownloadCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadEdBrowserDownloadCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadParticipatingSchoolsCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadParticipatingSchoolsCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadStudentTeacherGradeCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadStudentTeacherGradeCSV,
      payload,
    );
    return { blob, filename };
  };

  static downloadOtherCSMReportCSV = async (payload) => {
    const { blob, filename } = await authorizedFetchCSV(
      API_ENDPOINTS.downloadOtherCSMReportCSV,
      payload,
    );
    return { blob, filename };
  };

  /* ISAFE Subscription Admin */
  static getAdminUsers = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getAdminUsers,
      payload,
    );
    return responsePayload;
  };
  static addAdminUsers = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.addAdminUsers,
      payload,
    );
    return responsePayload;
  };
  static revokeAdminUsers = async (payload) => {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.revokeAdminUsers,
      payload,
    );
    return responsePayload;
  };

  /* SSO Logins*/
  static async googleSSO(payload) {
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.googleSSO,
      payload,
    );
    return responsePayload;
  }

  static async classLinkSSO(payload) {
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.classLinkSSO,
      payload,
    );
    return responsePayload;
  }

  static async microsoftSSO(payload) {
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.microsoftSSO,
      payload,
    );
    return responsePayload;
  }

  static async ssoLogin(payload) {
    const responsePayload = await authorizedFetchAnon(
      API_ENDPOINTS.ssoLogin,
      payload,
    );
    return responsePayload;
  }

  //Incident Reports
  static async getIncidentReport(payload) {
    const responsePayload = await authorizedFetch(
      API_ENDPOINTS.getIncidentReport,
      payload,
    );
    return responsePayload;
  }
}
export default API;
